import { ArrowRight, Handshake, GraduationCap, Sparkles, Building2 } from "lucide-react";

const OPPORTUNITIES = [
  { icon: GraduationCap, t: "School Partnerships" },
  { icon: Handshake,    t: "Sponsorship Opportunities" },
  { icon: Sparkles,     t: "Workshops & Pitch Competitions" },
  { icon: Building2,    t: "Workforce Development" },
];

/**
 * PartnerSection — bold yellow campaign block + branded illustration
 * supporting copy. QR card placeholder for in-room scans.
 */
export default function PartnerSection({ onPartner }) {
  return (
    <section
      id="partners"
      className="relative py-16 sm:py-20 px-3 sm:px-6"
      data-testid="partner-section"
    >
      <div className="relative max-w-7xl mx-auto rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden bg-[#FFD500] text-black shadow-[0_30px_60px_rgba(0,0,0,0.12)]">
        <span aria-hidden="true" className="absolute -top-10 -left-6 text-[10rem] sm:text-[14rem] opacity-15 lsbp-float-slow select-none">🍋</span>

        <div className="relative grid lg:grid-cols-12 gap-8 items-center px-6 sm:px-10 py-12 sm:py-14">
          {/* Copy */}
          <div className="lg:col-span-6">
            <p className="text-xs font-black tracking-[0.22em] uppercase mb-3">
              Bring This Program To Students
            </p>
            <h2 className="text-[32px] md:text-5xl font-black tracking-tighter leading-[1.05]">
              Sponsor a school.
              <br />
              <span className="bg-black text-[#FFD500] px-3 py-0.5 rounded-xl inline-block transform -rotate-1 mt-2">
                Fund a future.
              </span>
            </h2>
            <p className="mt-5 text-base md:text-xl text-black/80 max-w-md">
              Partner with us to bring entrepreneurship and responsible AI
              learning to schools, workforce programs, and youth communities.
            </p>

            <ul className="mt-6 grid grid-cols-2 gap-2 max-w-lg" data-testid="partner-opportunities">
              {OPPORTUNITIES.map((o) => (
                <li
                  key={o.t}
                  className="flex items-center gap-2 bg-white/40 backdrop-blur-sm ring-1 ring-black/10 rounded-xl px-3 py-2"
                >
                  <o.icon className="w-3.5 h-3.5 shrink-0" />
                  <span className="text-xs sm:text-sm font-extrabold leading-tight">{o.t}</span>
                </li>
              ))}
            </ul>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button
                onClick={onPartner}
                className="inline-flex items-center justify-center gap-2 bg-black text-[#FFD500] px-6 py-3.5 rounded-full text-lg font-bold hover:bg-white hover:text-black transition-colors"
                data-testid="partner-cta"
              >
                Partner With Us <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Right — image + QR */}
          <div className="lg:col-span-6 flex flex-col items-center gap-5">
            <div className="rounded-2xl overflow-hidden ring-2 ring-black/10 shadow-xl bg-black max-w-md">
              <img
                src="/assets/landing/partner-flyer.png"
                alt="Students and LemonBot promoting school partnerships and sponsorships for youth entrepreneurship education."
                className="w-full h-auto block"
                loading="lazy"
                data-testid="partner-feature-image"
              />
            </div>
            <div className="bg-white ring-1 ring-black/15 rounded-2xl p-5 w-56 text-center shadow-xl" data-testid="partner-qr-card">
              <div className="bg-white aspect-square rounded-xl ring-1 ring-black/10 mb-3 overflow-hidden p-1.5">
                <img
                  src="/assets/landing/partner-qr.png"
                  alt="QR code linking to the Lemonade Stand Business Plan sponsorship page."
                  className="w-full h-full object-contain"
                  loading="lazy"
                  data-testid="partner-qr-image"
                />
              </div>
              <p className="text-xs font-black uppercase tracking-widest text-gray-500 mb-1.5">
                Scan To Partner
              </p>
              <p className="text-sm font-extrabold text-black leading-tight">
                Bring this to your school
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
